import React from "react";
import { Button } from "../ui/button";
import { MAX_CAROUSEL_IMAGES } from "../../utils/consoleUtils";

const RUN_CAROUSEL_URL_ID = { add: { hero: "aru", scraped: "ars" }, edit: { hero: "eru", scraped: "ers" } };

/**
 * Hero + clubcard carousel image URLs for the run modal (deduped).
 * @param {{ hero_image?: string, clubcard_images?: Array<{ url?: string }> } | null | undefined} club
 * @returns {Array<{ url: string, caption: string }>}
 */
export function getClubHeroAndCarouselImageEntries(club) {
  if (!club) return [];
  const entries = [];
  const seen = new Set();
  const push = (rawUrl, caption) => {
    const u = rawUrl && String(rawUrl).trim();
    if (!u || !u.startsWith("http") || seen.has(u)) return;
    seen.add(u);
    entries.push({ url: u, caption });
  };
  push(club.hero_image, "Hero");
  (Array.isArray(club.clubcard_images) ? club.clubcard_images : []).forEach((img, i) => {
    push(img?.url, `Carousel ${i + 1}`);
  });
  return entries;
}

export function appendRunCarouselUrlSlot(setSlots, url, idPrefix, idx) {
  const u = typeof url === "string" ? url.trim() : "";
  if (!u.startsWith("http")) return;
  setSlots((prev) => {
    if (prev.some((s) => s.kind === "url" && s.url === u)) return prev;
    if (prev.length >= MAX_CAROUSEL_IMAGES) return prev;
    return [...prev, { id: `${idPrefix}-${Date.now()}-${idx}`, kind: "url", url: u }];
  });
}

/** User-pasted HTTPS URL; on save the backend fetches and stores in the meeting bucket. */
export function appendRunPastedUrlSlot(setSlots, url, idPrefix) {
  const u = typeof url === "string" ? url.trim() : "";
  if (!u.startsWith("http")) return;
  setSlots((prev) => {
    if (
      prev.some(
        (s) =>
          (s.kind === "url" || s.kind === "pasted_url") &&
          typeof s.url === "string" &&
          s.url.trim() === u
      )
    ) {
      return prev;
    }
    if (prev.length >= MAX_CAROUSEL_IMAGES) return prev;
    return [...prev, { id: `${idPrefix}-paste-${Date.now()}`, kind: "pasted_url", url: u }];
  });
}

export function removeRunCarouselUrlSlot(setSlots, url) {
  const target = typeof url === "string" ? url.trim() : "";
  setSlots((prev) =>
    prev.filter((s) => {
      if (s.kind !== "url" && s.kind !== "pasted_url") return true;
      const su = typeof s.url === "string" ? s.url.trim() : "";
      return su !== target;
    })
  );
}

export function appendRunImageFileSlots(setSlots, files, idPrefix) {
  setSlots((prev) => {
    const room = MAX_CAROUSEL_IMAGES - prev.length;
    if (room <= 0) return prev;
    const next = [...prev];
    let n = 0;
    for (const file of files) {
      if (n >= room) break;
      next.push({ id: `${idPrefix}-${Date.now()}-${n}-${file.name}`, kind: "file", file });
      n += 1;
    }
    return next;
  });
}

/** Club hero/carousel + scraped URL grids for add/edit run modals. */
export function RunClubCarouselUrlSections({
  club,
  slots,
  setSlots,
  mode,
  heroReorderHint,
  scrapedHint,
  onRemoveScrapedFromClub,
  scrapedRemoveBusyDisplayUrl,
}) {
  if (!club) return null;
  const carouselEntries = getClubHeroAndCarouselImageEntries(club);
  const scrapedRows = (Array.isArray(club.scraped_images) ? club.scraped_images : [])
    .map((stored) => (typeof stored === "string" ? stored : ""))
    .filter((stored) => stored.trim().startsWith("http"))
    .map((storedUrl) => ({
      storedUrl,
      displayUrl: storedUrl.trim(),
    }));
  if (carouselEntries.length === 0 && scrapedRows.length === 0) return null;
  const canAddMore = slots.length < MAX_CAROUSEL_IMAGES;
  const ids = RUN_CAROUSEL_URL_ID[mode] || RUN_CAROUSEL_URL_ID.add;
  const slotHasUrl = (u) =>
    slots.some(
      (s) =>
        (s.kind === "url" || s.kind === "pasted_url") &&
        typeof s.url === "string" &&
        s.url.trim() === u
    );

  return (
    <>
      {carouselEntries.length > 0 && (
        <div className="grid gap-2">
          <label className="text-sm font-medium text-[var(--console-ink)]">Club hero &amp; carousel</label>
          {heroReorderHint ? <p className="text-xs text-[var(--console-muted)]">{heroReorderHint}</p> : null}
          <div className="flex flex-wrap gap-3">
            {carouselEntries.map(({ url, caption }, idx) => {
              const isSelected = slotHasUrl(url);
              return (
                <div key={`${mode}-carousel-${url}-${idx}`} className="flex flex-col items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-[var(--console-muted)]">{caption}</span>
                  <img
                    src={url}
                    alt=""
                    className={`h-20 w-20 object-cover rounded border bg-[var(--console-surface)] ${isSelected ? "border-green-500 ring-1 ring-green-500" : "border-[var(--console-border)]"}`}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    disabled={isSelected || !canAddMore}
                    onClick={() => {
                      if (isSelected || !canAddMore) return;
                      appendRunCarouselUrlSlot(setSlots, url, ids.hero, idx);
                    }}
                  >
                    {isSelected ? "Added" : "Add to run"}
                  </Button>
                  {isSelected ? (
                    <button
                      type="button"
                      className="text-xs text-[var(--console-muted)] hover:underline"
                      onClick={() => removeRunCarouselUrlSlot(setSlots, url)}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {scrapedRows.length > 0 ? (
        <div className="grid gap-2">
          <label className="text-sm font-medium text-[var(--console-ink)]">Scraped images from club website</label>
          {scrapedHint ? <p className="text-xs text-[var(--console-muted)]">{scrapedHint}</p> : null}
          {onRemoveScrapedFromClub ? (
            <p className="text-xs text-[var(--console-muted)]">
              Remove from club drops it from this pool everywhere (edit club, run modals).
            </p>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {scrapedRows.map(({ storedUrl, displayUrl }, idx) => {
              const isSelected = slotHasUrl(displayUrl);
              const scrapedBusy = Boolean(scrapedRemoveBusyDisplayUrl);
              const busyHere = scrapedRemoveBusyDisplayUrl === displayUrl;
              return (
                <div key={`${mode}-scraped-${idx}-${displayUrl}`} className="flex flex-col items-center gap-1.5">
                  <img
                    src={displayUrl}
                    alt=""
                    className={`h-20 w-20 object-cover rounded border bg-[var(--console-surface)] ${isSelected ? "border-green-500 ring-1 ring-green-500" : "border-[var(--console-border)]"}`}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    disabled={isSelected || !canAddMore || scrapedBusy}
                    onClick={() => {
                      if (isSelected || !canAddMore || scrapedBusy) return;
                      appendRunCarouselUrlSlot(setSlots, displayUrl, ids.scraped, idx);
                    }}
                  >
                    {isSelected ? "Added" : "Add to run"}
                  </Button>
                  {isSelected ? (
                    <button
                      type="button"
                      className="text-xs text-[var(--console-muted)] hover:underline"
                      onClick={() => removeRunCarouselUrlSlot(setSlots, displayUrl)}
                    >
                      Remove
                    </button>
                  ) : null}
                  {onRemoveScrapedFromClub ? (
                    <button
                      type="button"
                      className="text-xs text-[var(--console-red)] hover:underline disabled:opacity-50"
                      disabled={scrapedBusy}
                      onClick={() => onRemoveScrapedFromClub(storedUrl, displayUrl)}
                    >
                      {busyHere ? "Removing…" : "Remove from club"}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}
